import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PatternIcon from "@mui/icons-material/Pattern";

export interface FAAMappingPattern {
  id: string;
  search_query: string;
  matched_aircraft_id: string;
  applied_count: number;
  last_applied_at: string | null;
  created_at: string;
  aircraft?: {
    make: string;
    model: string;
  };
}

export interface RecentPatternsPanelProps {
  /** List of saved patterns */
  patterns: FAAMappingPattern[];
  /** Whether patterns are loading */
  loading: boolean;
  /** Callback when re-apply is clicked */
  onReApply: (pattern: FAAMappingPattern) => void;
  /** Callback when delete is clicked */
  onDelete: (patternId: string) => void;
}

export const RecentPatternsPanel: React.FC<RecentPatternsPanelProps> = ({
  patterns,
  loading,
  onReApply,
  onDelete,
}) => {
  const [expanded, setExpanded] = React.useState<boolean>(false);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatAircraftName = (pattern: FAAMappingPattern) => {
    if (!pattern.aircraft) return "Unknown Aircraft";
    return `${pattern.aircraft.make} ${pattern.aircraft.model}`;
  };

  if (loading || patterns.length === 0) {
    return null;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ backgroundColor: "background.paper" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={2} alignItems="center">
          <PatternIcon color="primary" />
          <Typography variant="h6">Saved Patterns</Typography>
          <Chip
            label={patterns.length}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {patterns.map((pattern) => (
            <Grid item xs={12} sm={6} md={4} key={pattern.id}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      "{pattern.search_query}"
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {formatAircraftName(pattern)}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={`${pattern.applied_count} applied`}
                        size="small"
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">
                        Last: {formatDate(pattern.last_applied_at)}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
                <CardActions sx={{ justifyContent: "space-between", pt: 0 }}>
                  <Button
                    size="small"
                    startIcon={<AutorenewIcon />}
                    onClick={() => onReApply(pattern)}
                  >
                    Re-apply
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(pattern.id)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
